<template>
    <div class="c-switch-autocomplete">
        <div v-if="title || addingAddress" class="c-switch-autocomplete__header">
            <div v-if="title" class="c-switch-content__section__title">
                {{ title }}
            </div>

            <label class="c-switch-autocomplete__checkbox">
                <input
                    type="checkbox"
                    v-model="isInternationalMode"
                    @change="toggleInternationalMode"
                />
                <span class="c-switch-autocomplete__checkbox__box"></span>
                <span class="c-switch-autocomplete__checkbox__text">
                    {{ $t("Switch.Addresses.internationalTogglePrefix") }}
                    <strong>{{ $t("Switch.Addresses.internationalToggleCountries") }}</strong>
                </span>
            </label>
        </div>

        <div
            class="c-form__item c-form__item--text c-switch-autocomplete__input-wrapper"
            :class="{ 'is-international': isInternationalMode }"
        >
            <label v-if="!isInternationalMode" class="c-switch-autocomplete__label">
                {{ $t("Switch.Addresses.autocompleteLabel") }}
            </label>

            <input
                ref="input"
                type="text"
                name="custom_switch_address"
                :placeholder="inputPlaceholder"
                :class="`c-form__input ${instanceClass}`"
                autocomplete="off"
                maxlength="200"
                :value="inputValue"
                @focus="handleInputFocus"
                @input="handleInput"
                @blur="hideInternationalList"
            />

            <ul
                v-if="
                    isInternationalMode &&
                    isInternationalListVisible &&
                    filteredInternationalCities.length
                "
                class="c-switch-autocomplete__list"
            >
                <template v-for="country in groupedInternationalCities">
                    <li
                        :key="`country-${country.iso}`"
                        class="c-switch-autocomplete__list__country"
                    >
                        <span
                            class="c-switch-autocomplete__list__flag"
                            :class="`c-switch-autocomplete__list__flag--${country.iso.toLowerCase()}`"
                        ></span>
                        <span>{{ country.name }}</span>
                    </li>

                    <li
                        v-for="city in country.cities"
                        :key="city.id"
                        class="c-switch-autocomplete__list__item"
                        @mousedown.prevent="selectInternationalCity(city)"
                    >
                        {{ city.name }}
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import {
    initSmartForm as initSmartFormLib,
    cleanupSmartFormInstance,
    setCountryForSmartformInstance,
} from "Tools/smartform";

const COUNTRY_FALLBACKS = {
    AT: "Rakúsko",
    HU: "Maďarsko",
};
const COUNTRY_ORDER = ["HU", "AT"];

export default {
    props: {
        addingAddress: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            isSmartFormInitialized: false,
            isInternationalMode: false,
            isInternationalListVisible: false,
            internationalCities: [],
            internationalCountries: {},
            internationalQuery: "",
            internationalCitiesPromise: null,
            hideListTimeout: null,
        };
    },
    mounted() {
        // Initialize SmartForm when component mounts
        this.initSmartForm();
    },
    beforeDestroy() {
        // Clean up SmartForm instance
        cleanupSmartFormInstance(this.instanceId);

        // Clean up timeout
        this.cancelHideListTimeout();

        // Reset flag when component is destroyed so it reinitializes on remount
        this.isSmartFormInitialized = false;
    },
    computed: {
        ...mapState(["autocompleteValue", "wannaChangeAddress"]),
        instanceId() {
            // Create unique instance ID based on whether it's for adding address
            return this.addingAddress
                ? "smartform-instance-switch-modal-new"
                : "smartform-instance-switch-modal-existing";
        },
        instanceClass() {
            return `${this.instanceId} smartform-address-whole-address`;
        },
        inputValue() {
            if (this.isInternationalMode) {
                return this.internationalQuery;
            }

            if (this.wannaChangeAddress) {
                return "";
            }
            return this.autocompleteValue;
        },
        inputPlaceholder() {
            return this.isInternationalMode
                ? this.$t("Switch.Addresses.internationalPlaceholder")
                : this.$t("Switch.Addresses.autocompletePlaceholder");
        },
        filteredInternationalCities() {
            const query = this.normalizeSearchValue(this.internationalQuery);

            return this.internationalCities
                .filter((city) => {
                    const cityName = this.normalizeSearchValue(city.name);
                    const postCode = this.normalizeSearchValue(city.post_code);

                    return !query || cityName.includes(query) || postCode.includes(query);
                })
                .slice(0, 10);
        },
        groupedInternationalCities() {
            const grouped = {};

            this.filteredInternationalCities.forEach((city) => {
                const countryId = city.country_id;
                const iso = this.getCountryIso(city);
                const name = this.getCountryName(city);

                if (!grouped[countryId]) {
                    grouped[countryId] = {
                        iso,
                        name,
                        cities: [],
                    };
                }

                grouped[countryId].cities.push(city);
            });

            return Object.values(grouped).sort(this.sortCountries);
        },
    },
    methods: {
        handleInputFocus() {
            if (this.isInternationalMode) {
                this.isInternationalListVisible = true;
                this.loadInternationalCities();
                return;
            }

            this.initSmartForm();
        },
        handleInput(event) {
            if (!this.isInternationalMode) {
                return;
            }

            this.internationalQuery = event.target.value;
            this.isInternationalListVisible = true;
        },
        hideInternationalList() {
            this.hideListTimeout = setTimeout(() => {
                this.isInternationalListVisible = false;
            }, 150);
        },
        cancelHideListTimeout() {
            if (this.hideListTimeout) {
                clearTimeout(this.hideListTimeout);
                this.hideListTimeout = null;
            }
        },
        initSmartForm() {
            if (this.isSmartFormInitialized) {
                return;
            }

            this.isSmartFormInitialized = true;

            const tmpStore = this.$store;
            const tmpThis = this;

            initSmartFormLib(this.instanceId, function () {
                const smartformInstance = window.smartform.getInstance(tmpThis.instanceId);

                if (tmpThis.isInternationalMode) {
                    tmpThis.disableSmartForm();
                    return;
                }

                if (smartformInstance && smartformInstance.addressControl) {
                    // Set country to Slovakia (1 = SK)
                    setCountryForSmartformInstance(tmpThis.instanceId, 1);

                    // Add validation callback when address is selected
                    smartformInstance.addressControl.addValidationCallback(function (result) {
                        if (tmpThis.isInternationalMode) {
                            return;
                        }

                        const validationResult = result.result;

                        if (
                            validationResult.type ===
                            window.smartform.AddressValidationResultType.HIT
                        ) {
                            const addressArray = validationResult.addressArray;
                            if (addressArray && addressArray[0]) {
                                const address = addressArray[0];

                                // Set address data from SmartForm result
                                tmpStore.commit("SET_AUTOCOMPLETE_ADDRESS", {
                                    postCode: address.ZIP,
                                    city: address.CITY,
                                    street: address.FORMATTED_ADDRESS_FIRST_LINE,
                                    fullAddress: `${address.FORMATTED_ADDRESS_FIRST_LINE}, ${address.ZIP} ${address.CITY}`,
                                });

                                // Process the address (calls /switch-process-address)
                                tmpStore.dispatch("processAddress", tmpThis.addingAddress);
                            }
                        }
                    });
                }
            });
        },
        toggleInternationalMode() {
            this.internationalQuery = "";
            this.$store.commit("SET_PROCESS_ERROR_MESSAGE", false);

            if (this.isInternationalMode) {
                this.cancelHideListTimeout();
                this.disableSmartForm();
                this.isInternationalListVisible = true;
                this.loadInternationalCities();
                this.$nextTick(() => {
                    this.$refs.input?.focus();
                });
                return;
            }

            this.enableSmartForm();
            this.isInternationalListVisible = false;
        },
        disableSmartForm() {
            setCountryForSmartformInstance(this.instanceId, 0);
            this.isSmartFormInitialized = false;
        },
        enableSmartForm() {
            setCountryForSmartformInstance(this.instanceId, 1);
            this.initSmartForm();
        },
        loadInternationalCities() {
            if (this.internationalCities.length > 0) {
                return Promise.resolve(this.internationalCities);
            }

            if (this.internationalCitiesPromise) {
                return this.internationalCitiesPromise;
            }

            this.internationalCitiesPromise = fetch("/switch-fetch-international")
                .then((response) => response.json())
                .then((data) => {
                    if (data.status && data.cities) {
                        this.internationalCities = [];
                        this.internationalCountries = data.countries || {};

                        Object.keys(data.cities).forEach((countryId) => {
                            data.cities[countryId].forEach((city) => {
                                this.internationalCities.push(city);
                            });
                        });
                    }

                    return this.internationalCities;
                })
                .catch((error) => {
                    console.error("Error loading international cities:", error);
                    return [];
                })
                .finally(() => {
                    this.internationalCitiesPromise = null;
                });

            return this.internationalCitiesPromise;
        },
        normalizeCityValue(value) {
            return (value || "").toString();
        },
        normalizeSearchValue(value) {
            return this.normalizeCityValue(value)
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase();
        },
        getCountry(city) {
            return this.internationalCountries[city.country_id] || {};
        },
        getCountryIso(city) {
            return (this.getCountry(city).iso2code || "").toUpperCase();
        },
        getCountryName(city) {
            const country = this.getCountry(city);
            const iso = this.getCountryIso(city);

            return COUNTRY_FALLBACKS[iso] || country.name || "";
        },
        sortCountries(countryA, countryB) {
            const orderA = COUNTRY_ORDER.indexOf(countryA.iso);
            const orderB = COUNTRY_ORDER.indexOf(countryB.iso);

            if (orderA !== -1 || orderB !== -1) {
                return (orderA === -1 ? 99 : orderA) - (orderB === -1 ? 99 : orderB);
            }

            return countryA.name.localeCompare(countryB.name, "sk");
        },
        selectInternationalCity(city) {
            const cityName = this.normalizeCityValue(city.name);
            const postCode = this.normalizeCityValue(city.post_code);
            const fullAddress = `${cityName}, ${postCode}`;

            this.internationalQuery = fullAddress;
            this.isInternationalListVisible = false;

            this.$store.commit("SET_AUTOCOMPLETE_ADDRESS", {
                postCode,
                city: cityName,
                street: "",
                fullAddress,
            });

            this.$store.dispatch("processAddress", this.addingAddress);
        },
    },
};
</script>
